import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import type { ListMeta } from './adminUsers';

export type ManualTransactionKind = 'deposit' | 'withdraw';

/** The two outcomes the execute endpoint accepts. */
export type ManualExecutionStatus = 'SUCCESS' | 'FAILED';

export type ManualTransactionOwner = {
  id?: string;
  _id?: string;
  email?: string;
  name?: { first?: string; last?: string };
  bxlend_id?: string;
  status?: string;
};

export type ManualTransactionExecution = {
  isExecuted?: boolean;
  reason?: string | null;
  timestamp?: string | null;
  executor?: ManualTransactionOwner | string | null;
};

export type ManualTransaction = {
  id: string;
  kind?: string;
  status?: string;
  currency_code?: string;
  quantity?: string | number;
  description?: string;
  /** S3 object ids, fetched through the authenticated attachment endpoint. */
  attachments?: string[];
  owner?: ManualTransactionOwner | string | null;
  execution?: ManualTransactionExecution;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const getManualOwner = (
  tx?: ManualTransaction | null,
): ManualTransactionOwner | null => {
  const owner = tx?.owner;
  if (!owner || typeof owner === 'string') return null;
  return owner;
};

export const getManualOwnerId = (tx?: ManualTransaction | null) => {
  const owner = tx?.owner;
  if (!owner) return undefined;
  if (typeof owner === 'string') return owner;
  return owner.id ?? owner._id;
};

export const formatManualOwnerName = (owner?: ManualTransactionOwner | null) => {
  const name = [owner?.name?.first, owner?.name?.last].filter(Boolean).join(' ').trim();
  return name || owner?.email || '—';
};

export const getManualExecutor = (
  execution?: ManualTransactionExecution,
): ManualTransactionOwner | null => {
  const executor = execution?.executor;
  if (!executor || typeof executor === 'string') return null;
  return executor;
};

/** True once a decision has been recorded, so the actions are spent. */
export const isManualExecuted = (tx?: ManualTransaction | null) =>
  Boolean(tx?.execution?.isExecuted);

export const fetchManualTransactions = async (
  kind: ManualTransactionKind,
  params: { page?: number; limit?: number },
) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/manual-transactions/${kind}${buildQuery(params)}`,
  );
  return {
    transactions: (response.data.manual_transactions || []) as ManualTransaction[],
    meta: response.data.meta as ListMeta,
  };
};

export const fetchManualTransaction = async (
  kind: ManualTransactionKind,
  id: string,
) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/manual-transactions/${kind}/${id}`,
  );
  return response.data.manual_transaction as ManualTransaction;
};

/**
 * Records the decision. SUCCESS credits (deposit) or sends (withdraw); FAILED
 * rejects it. The API ignores a second call once `execution.isExecuted` is set,
 * so this is safe to retry but cannot be undone.
 */
export const executeManualTransaction = async (
  kind: ManualTransactionKind,
  id: string,
  payload: { status: ManualExecutionStatus; reason?: string },
) => {
  const response = await request.patch(
    `${AUTH_URL}/v1/admin/manual-transactions/${kind}/${id}/execute`,
    payload,
  );
  return response.data.manual_transaction as ManualTransaction;
};

/**
 * The attachment endpoint streams the raw image behind the admin token, so it
 * cannot be used as an `<img src>` directly — it is fetched as a blob and
 * handed back as an object URL the caller must revoke.
 */
export const fetchManualAttachmentUrl = async (
  transactionId: string,
  attachmentId: string,
) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/manual-transactions/${transactionId}/attachment/${attachmentId}`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(response.data as Blob);
};
